import React, { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { fetchOrders } from "../services/orderService";
import { fetchReturnRequests } from "../services/returnService";
import "../styles/pages/OrdersPage.css";
import { Order, ReturnRequest } from "../types/store";
import { formatCurrency } from "../utils/currency";

const ORDER_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "PROCESSING", label: "Processing" },
  { value: "OUT_FOR_DELIVERY", label: "Out for delivery" },
  { value: "DELIVERED", label: "Delivered" },
] as const;

type OrderFilterValue = (typeof ORDER_FILTERS)[number]["value"];
const ORDER_STATUS_FLOW = [
  "CONFIRMED",
  "PROCESSING",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "RETURN_REQUESTED",
  "REFUNDED",
] as const;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const formatStatus = (value: string) =>
  value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatShortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
  });

const getPrimaryItem = (order: Order) => order.items[0];

const getLatestReturnRequest = (returnRequests: ReturnRequest[], orderId: number) =>
  returnRequests
    .filter((request) => request.orderId === orderId)
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    CONFIRMED: "Confirmed",
    PROCESSING: "Processing",
    SHIPPED: "Shipped",
    OUT_FOR_DELIVERY: "Out for delivery",
    DELIVERED: "Delivered",
    RETURN_REQUESTED: "Return requested",
    UNDER_REVIEW: "Under review",
    READY_TO_PICKUP: "Return pickup",
    PICKUP_SCHEDULED: "Scheduled",
    PICKED_UP: "Returned",
    REFUNDED: "Refunded",
    REJECTED: "Rejected",
    REQUESTED: "Requested",
    APPROVED: "Approved",
    CLOSED: "Closed",
  };

  return labels[status] || formatStatus(status);
};


const isReturnComplete = (request?: ReturnRequest | undefined) =>
  Boolean(
    request &&
      (request.status === "REFUNDED" ||
        (request.requestType === "REPLACEMENT" && request.status === "DELIVERED")),
  );

const getOrderFilterValue = (order: Order, returnRequest?: ReturnRequest | undefined): string => {
  if (isReturnComplete(returnRequest)) {
    return "REFUNDED";
  }

  if (returnRequest) {
    return "RETURN_REQUESTED";
  }

  if (ORDER_STATUS_FLOW.includes(order.status as (typeof ORDER_STATUS_FLOW)[number])) {
    return order.status;
  }

  return "ALL";
};

const getOrderHeadline = (order: Order, returnRequest?: ReturnRequest | undefined) => {
  if (isReturnComplete(returnRequest)) {
    return returnRequest?.requestType === "REPLACEMENT" ? "Replacement delivered" : "Refund completed";
  }

  if (order.status === "DELIVERED") {
    return `Delivered on ${formatShortDate(order.deliveredAt ?? order.createdAt)}`;
  }

  return getStatusLabel(order.status);
};

const getOrderSubheadline = (order: Order, returnRequest?: ReturnRequest | undefined) => {
  const primaryItem = getPrimaryItem(order);

  if (isReturnComplete(returnRequest)) {
    return primaryItem ? primaryItem.productName : `Order ${order.orderNumber}`;
  }

  if (order.status === "DELIVERED") {
    return primaryItem ? primaryItem.productName : "Delivered order";
  }

  if (primaryItem) {
    return primaryItem.productName;
  }

  return `Order ${order.orderNumber}`;
};

const getBadgeTone = (order: Order, returnRequest?: ReturnRequest | undefined) => {
  if (isReturnComplete(returnRequest)) {
    return "success";
  }

  if (returnRequest) {
    return "warning";
  }

  if (order.status === "DELIVERED") {
    return "success";
  }

  if (order.status === "CANCELLED" || order.status === "REJECTED") {
    return "danger";
  }

  return "info";
};

const getBadgeLabel = (order: Order, returnRequest?: ReturnRequest | undefined) => {
  if (isReturnComplete(returnRequest)) {
    return returnRequest?.requestType === "REPLACEMENT" ? "Replacement delivered" : "Refund completed";
  }

  if (returnRequest) {
    return `${returnRequest.requestType === "REPLACEMENT" ? "Replacement" : "Return"}: ${getStatusLabel(returnRequest.status)}`;
  }

  if (order.status === "DELIVERED") {
    return "Delivered";
  }

  return getStatusLabel(order.status);
};

const getDisplayStatus = (order: Order, returnRequest?: ReturnRequest | undefined) => {
  if (isReturnComplete(returnRequest)) {
    return "REFUNDED";
  }

  if (returnRequest) {
    return "RETURN_REQUESTED";
  }

  return order.status;
};

const getReturnStatusCopy = (request: ReturnRequest) => {
  const actionLabel = request.requestType === "REPLACEMENT" ? "Replacement" : "Return";

  if (request.status === "REFUNDED") {
    if (request.requestType === "REPLACEMENT" || request.preferredResolution === "REPLACEMENT") {
      return "Replacement completed by support.";
    }

    if (request.preferredResolution === "WALLET_CREDIT") {
      return "Refund completed as wallet credit.";
    }

    if (request.preferredResolution === "MANUAL_REFUND") {
      return "Manual COD refund completed by support.";
    }

    return `${actionLabel} completed by support.`;
  }

  if (request.requestType === "REPLACEMENT" && request.status === "DELIVERED") {
    return "Replacement delivered by support.";
  }

  if (request.status === "REJECTED") {
    return `${actionLabel} rejected by support.`;
  }

  return `${actionLabel} status: ${getStatusLabel(request.status)}`;
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [returnRequests, setReturnRequests] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState<OrderFilterValue>("ALL");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const orderResponse = await fetchOrders();
        setOrders(orderResponse);
      } catch {
        setError("Unable to load your order history right now.");
      } finally {
        setLoading(false);
      }
    };

    void loadOrders();
  }, []);

  useEffect(() => {
    const loadReturnRequests = async () => {
      try {
        const response = await fetchReturnRequests();
        setReturnRequests(response);
      } catch {
        setReturnRequests([]);
      }
    };

    void loadReturnRequests();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders
      .slice()
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .filter((order) => {
        const returnRequest = getLatestReturnRequest(returnRequests, order.id);
        const filterValue = getOrderFilterValue(order, returnRequest);

        if (activeFilter !== "ALL" && filterValue !== activeFilter) {
          return false;
        }
        return true;
      });
  }, [activeFilter, orders, returnRequests]);

  const handleReorder = (order: Order) => {
    const primaryItem = getPrimaryItem(order);
    if (!primaryItem?.productSlug) {
      toast.error("Product details are unavailable for this order.");
      return;
    }

    navigate(`/products/${primaryItem.productSlug}`);
  };

  const openOrderDetails = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const handleOrderCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, orderId: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openOrderDetails(orderId);
    }
  };

  if (loading) {
    return (
      <section className="shell section page-section orders-page">
        <MobilePageHeader title="My Orders" />
        <div className="store-card empty-state">
          <h1>Loading your orders...</h1>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="shell section page-section orders-page">
        <MobilePageHeader title="My Orders" />
        <div className="store-card empty-state">
          <h1>Order history unavailable</h1>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  if (!orders.length) {
    return (
      <section className="shell section page-section orders-page">
        <MobilePageHeader title="My Orders" />
        <div className="store-card empty-state">
          <h1>No orders yet</h1>
          <p>Your future purchases will show up here with status and item details.</p>
          <Link className="button" to="/products">
            Start shopping
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="shell section page-section orders-page">
      <MobilePageHeader title="My Orders" />
      <div className="orders-page__header">
        <h1>My Orders</h1>

        <button
          className="orders-page__filters-button"
          type="button"
          onClick={() => setShowFilters((current) => !current)}
          aria-expanded={showFilters}
        >
          <span className="orders-page__filters-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span>Filters</span>
        </button>
      </div>

      <div className={showFilters ? "orders-page__filters is-open" : "orders-page__filters"}>
        {ORDER_FILTERS.map((filter) => (
          <button
            key={filter.value}
            className={activeFilter === filter.value ? "orders-page__chip is-active" : "orders-page__chip"}
            type="button"
            onClick={() => {
              setActiveFilter(filter.value);
              setShowFilters(false);
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {!filteredOrders.length ? (
        <div className="store-card empty-state">
          <h2>No matching orders</h2>
          <p>Try a different order number, product name, or filter.</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => {
            const returnRequest = getLatestReturnRequest(returnRequests, order.id);
            const primaryItem = getPrimaryItem(order);
            const badgeTone = getBadgeTone(order, returnRequest);
            const badgeLabel = getBadgeLabel(order, returnRequest);
            const headline = getOrderHeadline(order, returnRequest);
            const subheadline = getOrderSubheadline(order, returnRequest);
            const statusValue = getDisplayStatus(order, returnRequest);
            const refundAmount = returnRequest?.orderTotal ?? order.totalAmount;
            return (
              <article
                className="store-card order-card"
                key={order.id}
                role="button"
                tabIndex={0}
                onClick={() => openOrderDetails(order.id)}
                onKeyDown={(event) => handleOrderCardKeyDown(event, order.id)}
                aria-label={`Open details for order ${order.orderNumber}`}
              >
                <div className="mobile-order-card">
                  <div className="mobile-order-card__header">
                    <time>{new Date(order.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</time>
                    <div className="mobile-order-card__price">
                      <strong>{formatCurrency(order.totalAmount)}</strong>
                    </div>
                  </div>
                  <div className="mobile-order-card__images" aria-label={`${order.items.length} order items`}>
                    {order.items.slice(0, 4).map((item, itemIndex) => (
                      <span key={`${item.productSlug}-${itemIndex}`}>
                        <img src={item.image} alt={item.productName} />
                      </span>
                    ))}
                    {order.items.length > 4 ? <b>+{order.items.length - 4}</b> : null}
                  </div>
                  <div className="mobile-order-card__footer">
                    <span className={`order-card__badge order-card__badge--${badgeTone}`}>{badgeLabel}</span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReorder(order);
                      }}
                    >
                      Order again <span>↻</span>
                    </button>
                  </div>
                </div>
                <button
                  className="order-card__summary"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openOrderDetails(order.id);
                  }}
                  aria-label={`Open details for order ${order.orderNumber}`}
                >
                  <div className="order-card__media">
                    {primaryItem ? (
                      <img src={primaryItem.image} alt={primaryItem.productName} />
                    ) : (
                      <div className="order-card__image-fallback" aria-hidden="true" />
                    )}
                  </div>

                  <div className="order-card__summary-copy">
                    <div className="order-card__headline-row">
                      <div className="order-card__headline-copy">
                        <h2>{headline}</h2>
                        <p>{subheadline}</p>
                      </div>
                      <span className="order-card__chevron" aria-hidden="true" />
                    </div>

                    <div className="order-card__badge-row">
                      <span className={`order-card__badge order-card__badge--${badgeTone}`}>{badgeLabel}</span>
                      <span className="order-card__badge-copy">
                        {primaryItem ? `${order.items.length} item${order.items.length > 1 ? "s" : ""}` : "Order details"}
                      </span>
                    </div>
                  </div>
                </button>

                <div className="order-card__action-strip">
                  <span className="order-card__status-copy">Status: {getStatusLabel(statusValue)}</span>

                  <div className="order-card__post-delivery-actions">
                    <button
                      className="button button--light order-card__post-delivery-button"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReorder(order);
                      }}
                    >
                      Order again
                    </button>
                    {returnRequest ? (
                      <div className="order-card__refund-row order-card__refund-row--success">
                        <span className="order-card__refund-icon" aria-hidden="true">
                          {returnRequest.requestType === "REPLACEMENT" ? "↻" : "✓"}
                        </span>
                        <span>
                          {isReturnComplete(returnRequest)
                            ? returnRequest.requestType === "REPLACEMENT"
                              ? "Replacement delivered"
                              : `Refund of ${formatCurrency(refundAmount)}`
                            : getReturnStatusCopy(returnRequest)}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>

              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default OrdersPage;
